// File: src/components/OmniCine/Timeline/Clips/ClipBlock.tsx
// ----------------------------------------------------
// ClipBlock (A3.0 — Canonical Interaction Surface, reducer-safe)
// ----------------------------------------------------
// Render + interaction surface for a single clip.
// - Seconds-only domain
// - pxPerSec is the ONLY layout authority
// - Reducer-safe: ONLY dispatches actions that exist in timeline.types.ts
//   (clip/select, clip/move, clip/resize, clip/commit)
//
// Interaction is local (no clipInteraction in TimelineState).
// ----------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef } from "react";

import ThumbnailStrip from "@/components/OmniCine/Tracks/Shared/ThumbnailStrip";
import { resolveMediaIdentityTitle } from "@/components/OmniCine/media/resolveMediaTitle";
import { snapTime as snapTimelineTime } from "@/lib/omnicine/snapTime";
import {
  emitOmniCineClipSelected,
  emitOmniCineClipUrlResolved,
} from "@/lib/omnistudio/bridge/omnistudioBridgeContract";
import type { TimelineAction, TimelineClip } from "@/state/timeline/timeline.types";
import { useOmniCineTimeline } from "@/state/useOmniCineTimeline";

import ClipChrome from "./ClipChrome";
import ClipDuckOverlay from "./ClipDuckOverlay";

import {
  PerceptBadge,
  PerceptStripe,
  getActiveBreaksConstancyTransform,
  getNowSecFromState,
} from "./PerceptIndicators";

/* ------------------------------------------------------------------ */
/* Helper: robust URL resolution (Phase M1 preview → upgrade)          */
/* Priority: direct fields → mediaId-as-url → registry(mediaId) → none */
/* - Never clears a previously working URL just because registry fails */
/* - Caches SUCCESS only (retry-friendly)                              */
/* - Retries while "resolving…" (emulator / slow Firestore friendly)   */
/* - Emits optional upgrade hint: "omnicine:clip:url:resolved"         */
/* ------------------------------------------------------------------ */

const __mediaUrlCache = new Map<string, string>(); // ✅ success-only cache

function readStr(v: unknown): string {
  return typeof v === "string" && v.trim() ? v.trim() : "";
}

function looksLikeDirectUrlOrPath(s: string): boolean {
  const v = s.trim();
  if (!v) return false;
  if (v.startsWith("http://") || v.startsWith("https://")) return true;
  if (v.startsWith("blob:") || v.startsWith("data:")) return true;
  if (v.startsWith("/")) return true;
  return false;
}

function useClipResolvedUrlM1(clip: any): { resolvedUrl: string | null; note: string } {
  const clipId = readStr(clip?.id);
  const kind = readStr(clip?.kind);
  const mediaId = readStr(clip?.mediaId);

  // direct fields written by TimelineShell (previewUrl OR upgraded downloadUrl)
  const directUrl = useMemo(() => {
    return (
      readStr(clip?.url) ||
      readStr(clip?.mediaUrl) ||
      readStr(clip?.src) ||
      ""
    );
  }, [clip?.url, clip?.mediaUrl, clip?.src]);

  const [resolvedUrl, setResolvedUrl] = React.useState<string | null>(() => {
    if (directUrl) return directUrl;

    if (mediaId && looksLikeDirectUrlOrPath(mediaId)) return mediaId;

    const cached = mediaId ? __mediaUrlCache.get(mediaId) : "";
    if (cached) return cached;

    return null;
  });

  const [note, setNote] = React.useState<string>("");

  // Never "nuke" a good URL
  const lastGoodRef = React.useRef<string | null>(resolvedUrl);
  React.useEffect(() => {
    if (resolvedUrl) lastGoodRef.current = resolvedUrl;
  }, [resolvedUrl]);

  // retry tick (forces effect re-run without mutating timeline state)
  const [retryTick, setRetryTick] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    let retryTimer: any = null;

    async function run() {
      // 1) Direct URL always wins (preview or already-upgraded)
      if (directUrl) {
        if (!alive) return;
        lastGoodRef.current = directUrl;
        setResolvedUrl((prev) => (prev === directUrl ? prev : directUrl));
        setNote("");
        return;
      }

      // 2) If mediaId is itself a direct URL/path, use it
      if (mediaId && looksLikeDirectUrlOrPath(mediaId)) {
        if (!alive) return;
        __mediaUrlCache.set(mediaId, mediaId);
        lastGoodRef.current = mediaId;
        setResolvedUrl((prev) => (prev === mediaId ? prev : mediaId));
        setNote("");
        return;
      }

      // 3) No direct URL and no registry key → nothing
      if (!mediaId) {
        if (!alive) return;
        setNote(lastGoodRef.current ? "" : "no media URL");
        return;
      }

      // 4) Cached success?
      const cached = __mediaUrlCache.get(mediaId) || "";
      if (cached) {
        if (!alive) return;
        lastGoodRef.current = cached;
        setResolvedUrl((prev) => (prev === cached ? prev : cached));
        setNote("");
        return;
      }

      // 5) Registry resolve (retry-friendly)
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const w: any = window as any;
        const resolver = w?.__oc_media?.resolveRegistryToUrl;

        if (typeof resolver !== "function") {
          if (!alive) return;
          setNote(lastGoodRef.current ? "" : "no media URL");
          return;
        }

        if (!lastGoodRef.current) setNote("resolving…");

        const out = await resolver({ v: 1, provider: "registry", key: mediaId, kind });
        if (!alive) return;

        const urlOut = readStr(out);
        if (urlOut) {
          __mediaUrlCache.set(mediaId, urlOut);
          lastGoodRef.current = urlOut;
          setResolvedUrl((prev) => (prev === urlOut ? prev : urlOut));
          setNote("");

          if (clipId) {
            emitOmniCineClipUrlResolved({ clipId, mediaId, url: urlOut, kind, source: "ClipBlock" });
          }
          return;
        }

        // no URL yet (doc exists but not resolvable, or eventual consistency)
        if (!lastGoodRef.current) {
          setNote("resolving…");
          retryTimer = setTimeout(() => {
            if (!alive) return;
            setRetryTick((x) => x + 1);
          }, 350);
        } else {
          setNote("");
        }
      } catch {
        if (!alive) return;

        if (!lastGoodRef.current) {
          setNote("resolving…");
          retryTimer = setTimeout(() => {
            if (!alive) return;
            setRetryTick((x) => x + 1);
          }, 350);
        } else {
          setNote("");
        }
      }
    }

    run();

    return () => {
      alive = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [clipId, kind, mediaId, directUrl, retryTick]);

  return { resolvedUrl, note };
}

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */

type ClipBlockProps = {
  clip: TimelineClip;
  pxPerSec: number;
  viewportStartSec: number;
  selected?: boolean; // legacy (ignored)
  dispatch: React.Dispatch<TimelineAction>;
  compact?: boolean;
};

type NarrativeClipBadge = {
  id: string;
  label: string;
  tone: "scene" | "range";
};

type RangeAnchorBadge = {
  id: string;
  label: string;
  followUpdates: boolean;
};

const EDGE_HANDLE_PX = 8;
const MIN_LEN_SEC = 0.05;

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

function getNarrativeClipBadge(overlays: any[], clip: TimelineClip): NarrativeClipBadge | null {
  if (!Array.isArray(overlays) || !overlays.length) return null;

  const clipStart = Number(clip.startSec ?? 0);
  const clipEnd = Number(clip.endSec ?? clipStart);

  const sceneMarkers = overlays
    .filter((overlay) => {
      if (!overlay || overlay.kind !== "scene_marker") return false;
      return typeof overlay.timeSec === "number" && overlay.timeSec >= clipStart && overlay.timeSec <= clipEnd;
    })
    .sort((left, right) => Number(left.timeSec ?? 0) - Number(right.timeSec ?? 0));

  if (sceneMarkers.length > 0) {
    const overlay = sceneMarkers[0];
    return {
      id: String(overlay.id ?? `${clip.id}-scene-marker`),
      label: String(overlay.label ?? "Narrative"),
      tone: "scene",
    };
  }

  const arcRanges = overlays
    .filter((overlay) => {
      if (!overlay || overlay.kind !== "arc_range") return false;
      const startSec = Number(overlay.startSec ?? 0);
      const endSec = Number(overlay.endSec ?? startSec);
      return endSec >= clipStart && startSec <= clipEnd;
    })
    .sort((left, right) => Number(left.startSec ?? 0) - Number(right.startSec ?? 0));

  if (arcRanges.length > 0) {
    const overlay = arcRanges[0];
    return {
      id: String(overlay.id ?? `${clip.id}-arc-range`),
      label: String(overlay.label ?? "Narrative Arc"),
      tone: "range",
    };
  }

  return null;
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function finiteNum(x: unknown): x is number {
  return typeof x === "number" && Number.isFinite(x);
}

function emit(type: string, detail?: any) {
  window.dispatchEvent(new CustomEvent(type, { detail }));
}

function snapTimeSec(t: number, snap: any, altKey: boolean): number {
  if (altKey) return t;

  return snapTimelineTime(t, {
    enabled: !!snap?.enabled,
    mode: snap?.mode === "frame" || snap?.mode === "second" || snap?.mode === "marker" || snap?.mode === "beat" ? snap.mode : "second",
    fps: finiteNum(snap?.fps) ? Math.max(1, Math.min(240, snap.fps)) : 30,
    bpm: finiteNum(snap?.bpm) ? snap.bpm : undefined,
    beatDiv: finiteNum(snap?.beatDiv) ? snap.beatDiv : undefined,
    markers: Array.isArray(snap?.markers) ? snap.markers : undefined,
  });
}

type DragState =
  | {
      mode: "move";
      pointerId: number;
      startClientX: number;
      startStartSec: number;
      lenSec: number;
    }
  | {
      mode: "trim";
      edge: "start" | "end";
      pointerId: number;
      startClientX: number;
      startStartSec: number;
      startEndSec: number;
    }
  | null;

export default function ClipBlock({
  clip,
  pxPerSec,
  viewportStartSec,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selected = false,
  dispatch,
  compact = false,
}: ClipBlockProps): JSX.Element {
  const { state, selectedClipId, clipMediaMetadataByClipId } = useOmniCineTimeline();

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragState>(null);

  // ✅ Phase M1 robust media resolution (preview → upgrade)
  const { resolvedUrl, note: mediaResolveNote } = useClipResolvedUrlM1(clip as any);
  const mediaId = readStr((clip as any)?.mediaId);
  const clipMetadata = clipMediaMetadataByClipId[clip.id] ?? null;

  /* -------------------------------------------------- */
  /* Perception transforms (breaks-constancy) (safe)      */
  /* -------------------------------------------------- */

  const nowSec = useMemo(() => getNowSecFromState(state as any), [state]);
  const activeBreak = useMemo(
    () => getActiveBreaksConstancyTransform(state as any, clip.id, nowSec),
    [state, clip.id, nowSec],
  );

  /* -------------------------------------------------- */
  /* Derived layout (viewport-relative)                  */
  /* -------------------------------------------------- */

  const leftPx = useMemo(
    () => (clip.startSec - viewportStartSec) * pxPerSec,
    [clip.startSec, viewportStartSec, pxPerSec],
  );

  const widthPx = useMemo(
    () => Math.max(2, (clip.endSec - clip.startSec) * pxPerSec),
    [clip.startSec, clip.endSec, pxPerSec],
  );

  /* -------------------------------------------------- */
  /* Visual label + tooltip                              */
  /* -------------------------------------------------- */

  const label = useMemo(() => {
    return resolveMediaIdentityTitle({
      clipTitle:
        (typeof (clip as any)?.title === "string" && (clip as any).title) ||
        (typeof (clip as any)?.label === "string" && (clip as any).label) ||
        (typeof clipMetadata?.sourceLabel === "string" && clipMetadata.sourceLabel) ||
        null,
      assetDisplayName:
        (typeof clipMetadata?.fileName === "string" && clipMetadata.fileName) ||
        (typeof (clip as any)?.fileName === "string" && (clip as any).fileName) ||
        (typeof (clip as any)?.filename === "string" && (clip as any).filename) ||
        (typeof (clip as any)?.name === "string" && (clip as any).name) ||
        null,
      importedLabel: clipMetadata?.sourceLabel ?? null,
      mediaUrl: resolvedUrl ?? null,
      mediaId: mediaId || null,
      kind: String((clip as any)?.kind ?? clipMetadata?.kind ?? "unknown"),
    });
  }, [clip, clipMetadata, mediaId, resolvedUrl]);

  const bg = useMemo(() => {
    const k = String((clip as any)?.kind ?? "");
      if (k === "audio") return "linear-gradient(135deg,rgba(226,232,240,0.22),rgba(56,189,248,0.18) 52%,rgba(15,23,42,0.4))";
    if (k === "npc") return "linear-gradient(135deg,#f472b6,#a855f7)";
    return "linear-gradient(135deg,#22d3ee,#6366f1)";
  }, [clip]);

  const clipKind = String((clip as any)?.kind ?? clipMetadata?.kind ?? "");
  const showVideoThumbnails = useMemo(
    () => clipKind === "video" && !!resolvedUrl && widthPx >= 96,
    [clipKind, resolvedUrl, widthPx],
  );
  const thumbnailDurationSec = useMemo(() => Math.max(0.1, clip.endSec - clip.startSec), [clip.endSec, clip.startSec]);

  const snapHint = useMemo(() => {
    const on = !!(state as any)?.snap?.enabled;
    return on ? "Snap on (Alt disables)" : "Snap off";
  }, [(state as any)?.snap?.enabled]);

  const title = useMemo(() => {
    const dur = Math.max(0, clip.endSec - clip.startSec);
    const m = mediaId ? ` • mediaId=${mediaId}` : "";
    const u = resolvedUrl ? ` • url=ready` : "";
    return `${label} • ${dur.toFixed(2)}s • ${snapHint}${m}${u}`;
  }, [label, clip.startSec, clip.endSec, snapHint, mediaId, resolvedUrl]);

  const narrativeClipBadge = useMemo(() => {
    const preview = (state as any)?.narrativePreview;
    if (!preview?.active || !Array.isArray(preview.overlays)) return null;
    return getNarrativeClipBadge(preview.overlays, clip);
  }, [state, clip]);

  const rangeAnchorBadge = useMemo<RangeAnchorBadge | null>(() => {
    const anchor = (clip as any)?.rangeAnchor ?? null;
    const rangeId = readStr(anchor?.rangeId);
    if (!rangeId) return null;

    const boundRange = Array.isArray((state as any)?.ranges)
      ? (state as any).ranges.find((range: any) => String(range?.id ?? "") === rangeId)
      : null;
    const label = readStr(boundRange?.label) || readStr(boundRange?.kind) || "Anchored Range";

    return {
      id: rangeId,
      label,
      followUpdates: anchor?.followUpdates !== false,
    };
  }, [clip, state]);

  /* -------------------------------------------------- */
  /* Selection proof (provider UI selection)             */
  /* -------------------------------------------------- */

  const isSelected = String(selectedClipId ?? "") === String(clip.id);

  const selectClip = useCallback(() => {
    dispatch({
      type: "clip/select",
      payload: { clipId: clip.id },
      meta: { transient: true, source: "user" },
    });
    emitOmniCineClipSelected(String(clip.id), "ui");
  }, [dispatch, clip.id]);

  /* -------------------------------------------------- */
  /* Deterministic DOM probeability for selection         */
  /* -------------------------------------------------- */

  useEffect(() => {
    if (!isSelected) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w: any = window as any;
    w.__oc_debug = w.__oc_debug || {};

    const el = wrapperRef.current;
    w.__oc_debug.lastSelectedClipEl = el ?? null;
    w.__oc_debug.lastSelectedClipId = clip.id;

    const raf = requestAnimationFrame(() => {
      const node = wrapperRef.current;
      if (!node) return;

      try {
        const rect = node.getBoundingClientRect();
        w.__oc_debug.lastSelectedClipRect = {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        };
      } catch {
        w.__oc_debug.lastSelectedClipRect = null;
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [clip.id, isSelected]);

  /* -------------------------------------------------- */
  /* Pointer capture helpers                              */
  /* -------------------------------------------------- */

  const capturePointer = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = rootRef.current;
    if (!el) return false;
    try {
      el.setPointerCapture(e.pointerId);
      return true;
    } catch {
      return false;
    }
  }, []);

  const releasePointer = useCallback((pointerId: number) => {
    const el = rootRef.current;
    if (!el) return;
    try {
      el.releasePointerCapture(pointerId);
    } catch {
      // ignore
    }
  }, []);

  /* -------------------------------------------------- */
  /* Interaction begins                                  */
  /* -------------------------------------------------- */

  const beginMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      if ((clip as any).locked) return;

      e.stopPropagation();
      e.preventDefault();

      if (!capturePointer(e)) return;

      selectClip();

      const lenSec = Math.max(MIN_LEN_SEC, clip.endSec - clip.startSec);

      dragRef.current = {
        mode: "move",
        pointerId: e.pointerId,
        startClientX: e.clientX,
        startStartSec: clip.startSec,
        lenSec,
      };
    },
    [clip, capturePointer, selectClip],
  );

  const beginTrim = useCallback(
    (edge: "start" | "end", e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      if ((clip as any).locked) return;

      e.stopPropagation();
      e.preventDefault();

      if (!capturePointer(e)) return;

      selectClip();

      dragRef.current = {
        mode: "trim",
        edge,
        pointerId: e.pointerId,
        startClientX: e.clientX,
        startStartSec: clip.startSec,
        startEndSec: clip.endSec,
      };
    },
    [clip, capturePointer, selectClip],
  );

  /* -------------------------------------------------- */
  /* Pointer move/up/cancel                               */
  /* -------------------------------------------------- */

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = dragRef.current;
      if (!d) return;
      if (e.pointerId !== d.pointerId) return;

      const dx = e.clientX - d.startClientX;
      const dt = pxPerSec > 0 ? dx / pxPerSec : 0;

      const snapCfg = {
        ...((state as any)?.snap ?? {}),
        markers: Array.isArray((state as any)?.markers) ? (state as any).markers : [],
      };
      const durationSec = finiteNum((state as any)?.durationSec) ? Number((state as any).durationSec) : 0;

      if (d.mode === "move") {
        const rawStart = d.startStartSec + dt;
        const snappedStart = snapTimeSec(rawStart, snapCfg, !!e.altKey);
        const startSec = clamp(snappedStart, 0, Math.max(0, durationSec - d.lenSec));

        dispatch({
          type: "clip/move",
          payload: { clipId: clip.id, startSec },
          meta: { transient: true, source: "user" },
        });

        return;
      }

      if (d.mode === "trim") {
        if (d.edge === "start") {
          const rawStart = d.startStartSec + dt;
          const snapped = snapTimeSec(rawStart, snapCfg, !!e.altKey);

          const maxStart = Math.max(0, d.startEndSec - MIN_LEN_SEC);
          const startSec = clamp(snapped, 0, Math.min(maxStart, durationSec));

          dispatch({
            type: "clip/resize",
            payload: { clipId: clip.id, edge: "start", timeSec: startSec },
            meta: { transient: true, source: "user" },
          });

          return;
        }

        // edge === "end"
        const rawEnd = d.startEndSec + dt;
        const snapped = snapTimeSec(rawEnd, snapCfg, !!e.altKey);

        const minEnd = d.startStartSec + MIN_LEN_SEC;
        const endSec = clamp(snapped, Math.min(durationSec, minEnd), durationSec);

        dispatch({
          type: "clip/resize",
          payload: { clipId: clip.id, edge: "end", timeSec: endSec },
          meta: { transient: true, source: "user" },
        });
      }
    },
    [clip.id, dispatch, pxPerSec, state],
  );

  const endDrag = useCallback(
    (pointerId: number, note: string) => {
      dragRef.current = null;
      releasePointer(pointerId);

      dispatch({
        type: "clip/commit",
        payload: { clipId: clip.id },
        meta: { transient: false, source: "user", note },
      });
    },
    [dispatch, clip.id, releasePointer],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = dragRef.current;
      if (!d) return;
      if (e.pointerId !== d.pointerId) return;
      endDrag(d.pointerId, "pointerup");
    },
    [endDrag],
  );

  const onPointerCancel = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = dragRef.current;
      if (!d) return;
      if (e.pointerId !== d.pointerId) return;
      endDrag(d.pointerId, "pointercancel");
    },
    [endDrag],
  );

  /* -------------------------------------------------- */
  /* Escape cancels local drag (no reducer drift)         */
  /* -------------------------------------------------- */

  useEffect(() => {
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key !== "Escape") return;
      const d = dragRef.current;
      if (!d) return;

      if (d.mode === "move") {
        dispatch({
          type: "clip/move",
          payload: { clipId: clip.id, startSec: d.startStartSec },
          meta: { transient: false, source: "user", note: "escape cancel move" },
        });
      } else {
        dispatch({
          type: "clip/resize",
          payload: { clipId: clip.id, edge: "start", timeSec: d.startStartSec },
          meta: { transient: true, source: "user", note: "escape cancel trim start" },
        });

        dispatch({
          type: "clip/resize",
          payload: { clipId: clip.id, edge: "end", timeSec: d.startEndSec },
          meta: { transient: false, source: "user", note: "escape cancel trim end" },
        });
      }

      endDrag(d.pointerId, "escape");
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [clip.id, dispatch, endDrag]);

  /* -------------------------------------------------- */
  /* Unmount cleanup                                     */
  /* -------------------------------------------------- */

  useEffect(() => {
    return () => {
      const d = dragRef.current;
      if (d) {
        dragRef.current = null;
        releasePointer(d.pointerId);
      }
    };
  }, [releasePointer]);

  return (
    <div
      ref={wrapperRef}
      className={cx(
        "absolute top-0 z-30 h-full",
        isSelected
          ? "ring-2 ring-cyan-300/70 shadow-[0_0_0_1px_rgba(34,211,238,0.12),0_16px_40px_rgba(0,0,0,0.35)]"
          : "",
      )}
      {...(isSelected ? { "data-oc-clip-selected": "1" } : {})}
      data-oc-clip-id={clip.id}
      data-oc-track-id={clip.trackId}
      data-oc-clip-kind={String((clip as any)?.kind ?? "unknown")}
      style={{ left: leftPx, width: widthPx }}
    >
      <ClipChrome clipId={clip.id}>
        <PerceptStripe transform={activeBreak} />
        <PerceptBadge
          transform={activeBreak}
          align="right"
          onOpenTransform={(t) => {
            window.dispatchEvent(
              new CustomEvent("omnicine:percept:open", {
                detail: { clipId: clip.id, transform: t },
              }),
            );
          }}
        />

        <div
          ref={rootRef}
          className="relative z-[10] h-full select-none touch-none"
          title={title}
          aria-label={`Timeline clip ${label}`}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
        >
          {showVideoThumbnails ? (
            <div className="absolute inset-0 overflow-hidden rounded-[inherit]">
              <ThumbnailStrip src={resolvedUrl ?? ""} durationSec={thumbnailDurationSec} height={72} provider="video" />
              <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(34,211,238,0.22),rgba(99,102,241,0.18))]" />
              <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.06),rgba(2,6,23,0.22))]" />
            </div>
          ) : (
            <div
              className="absolute inset-0"
              style={{
                background: bg,
                opacity: compact ? 0.85 : 1,
              }}
            />
          )}

          <div
            className="absolute left-0 top-0 h-full cursor-ew-resize"
            style={{ width: EDGE_HANDLE_PX }}
            onPointerDown={(e) => beginTrim("start", e)}
            aria-label="Trim start"
            title="Trim start (Alt disables snap)"
          />
          <div
            className="absolute right-0 top-0 h-full cursor-ew-resize"
            style={{ width: EDGE_HANDLE_PX }}
            onPointerDown={(e) => beginTrim("end", e)}
            aria-label="Trim end"
            title="Trim end (Alt disables snap)"
          />

          <div
            className="absolute inset-y-0"
            style={{ left: EDGE_HANDLE_PX, right: EDGE_HANDLE_PX }}
            onPointerDown={beginMove}
            aria-label="Move clip"
            title="Move (Alt disables snap)"
          />

          {!compact && (
            <div className="relative z-10 flex items-center gap-2 truncate px-2 py-1 text-[11px] text-white/90">
              <span className="truncate">{label}</span>

              {narrativeClipBadge ? (
                <span
                  className={cx(
                    "max-w-[42%] truncate rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] backdrop-blur-sm",
                    narrativeClipBadge.tone === "scene"
                      ? "border-fuchsia-300/24 bg-fuchsia-500/12 text-fuchsia-50"
                      : "border-cyan-300/24 bg-cyan-400/10 text-cyan-50/90",
                  )}
                  title={narrativeClipBadge.label}
                  data-oc-clip-narrative-badge={narrativeClipBadge.id}
                >
                  {narrativeClipBadge.label}
                </span>
              ) : null}

              {rangeAnchorBadge ? (
                <span
                  className="max-w-[42%] truncate rounded-full border border-emerald-300/24 bg-emerald-500/12 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-50"
                  title={rangeAnchorBadge.followUpdates ? `${rangeAnchorBadge.label} · follows updates` : `${rangeAnchorBadge.label} · static anchor`}
                  data-oc-clip-range-anchor={rangeAnchorBadge.id}
                >
                  {rangeAnchorBadge.followUpdates ? `Anchor · ${rangeAnchorBadge.label}` : `Anchor Hold · ${rangeAnchorBadge.label}`}
                </span>
              ) : null}

              {mediaId && !resolvedUrl && mediaResolveNote === "no media URL" ? (
                <span className="ml-2 rounded border border-rose-500/30 bg-rose-500/20 px-2 py-0.5 text-[11px] text-rose-100">
                  no media URL
                </span>
              ) : null}

              {!resolvedUrl && mediaResolveNote === "resolving…" ? (
                <span className="ml-2 rounded border border-white/15 bg-white/10 px-2 py-0.5 text-[11px] text-white/80">
                  resolving…
                </span>
              ) : null}
            </div>
          )}

          <ClipDuckOverlay
            clipStart={clip.startSec}
            clipDur={Math.max(0, clip.endSec - clip.startSec)}
            pxPerSec={pxPerSec}
            regions={undefined}
          />
        </div>
      </ClipChrome>
    </div>
  );
}
